import React from "react";
import axios from "axios";
import { Upload, Sparkles, Loader2, RefreshCw, Download, Wand2 } from "lucide-react";
import { toast } from "sonner";
import VisualizerGate, { readGate } from "./VisualizerGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STYLES = [
  { key: "moderno", label: "Moderno", desc: "Linee pulite, luci led, tonalità chiare" },
  { key: "classico", label: "Classico Italiano", desc: "Parquet, dettagli in legno" },
  { key: "industriale", label: "Industriale", desc: "Cemento, metallo, mattoni" },
  { key: "scandinavo", label: "Scandinavo", desc: "Bianchi, legno chiaro, tessili" },
  { key: "rustico", label: "Rustico Mediterraneo", desc: "Travi, pietra, tonalità terrose" },
];

const STAGES = [
  "Analisi della stanza...",
  "Studio della prospettiva...",
  "Selezione materiali e luci...",
  "Generazione design fotorealistico...",
];

export default function Visualizer() {
  const inputRef = React.useRef(null);
  const [unlocked, setUnlocked] = React.useState(() => Boolean(readGate()));
  const [leadEmail, setLeadEmail] = React.useState(() => readGate()?.email || "");
  const [file, setFile] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [style, setStyle] = React.useState("moderno");
  const [notes, setNotes] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [stageIdx, setStageIdx] = React.useState(0);
  const [resultB64, setResultB64] = React.useState(null);
  const [resultMime, setResultMime] = React.useState("image/png");

  React.useEffect(() => {
    if (!loading) return;
    setStageIdx(0);
    const id = setInterval(() => {
      setStageIdx((i) => Math.min(i + 1, STAGES.length - 1));
    }, 2200);
    return () => clearInterval(id);
  }, [loading]);

  const onPick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine valida");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 10MB)");
      return;
    }
    setFile(f);
    setResultB64(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onPick(f);
  };

  const generate = async () => {
    if (!file) return toast.error("Carica prima la foto della stanza");
    setLoading(true);
    setResultB64(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("style", style);
      fd.append("notes", notes);
      if (leadEmail) fd.append("email", leadEmail);
      const res = await axios.post(`${API}/visualize`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResultB64(res.data.image_base64);
      setResultMime(res.data.mime_type || "image/png");
      toast.success("Ristrutturazione generata!");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Errore generico";
      toast.error(`Generazione fallita: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResultB64(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const download = () => {
    if (!resultB64) return;
    const a = document.createElement("a");
    a.href = `data:${resultMime};base64,${resultB64}`;
    a.download = `edil-murgia-restyle-${style}.png`;
    a.click();
  };

  return (
    <section id="visualizzatore" className="py-20 md:py-28 bg-brand-paper/40" data-testid="visualizer-section">
      <div className="container-page">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow flex items-center gap-2">
              <Wand2 className="h-3.5 w-3.5" /> Esclusiva · Visualizzatore AI
            </span>
            <h2 className="heading-display text-3xl md:text-5xl mt-4 leading-[1.05]">
              Carica una foto.
              <br /> Vedi la <span className="text-brand-terracotta">tua stanza ristrutturata</span> in 60 secondi.
            </h2>
            <p className="text-brand-mute mt-5 leading-relaxed">
              Una piccola anteprima creativa generata da AI per immaginare insieme
              il risultato finale. Non sostituisce un sopralluogo, ma è un ottimo
              punto di partenza prima di chiamarci per il preventivo.
            </p>

            <div className="mt-8 space-y-3">
              <label className="text-sm font-semibold text-brand-ink">Scegli lo stile</label>
              <div className="grid grid-cols-2 gap-2">
                {STYLES.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setStyle(s.key)}
                    className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                      style === s.key
                        ? "border-brand-terracotta bg-brand-terracotta/5"
                        : "border-brand-line hover:border-brand-ink/30"
                    }`}
                    data-testid={`style-${s.key}`}
                  >
                    <div className="text-sm font-semibold">{s.label}</div>
                    <div className="text-xs text-brand-mute mt-0.5">{s.desc}</div>
                  </button>
                ))}
              </div>

              <label className="text-sm font-semibold text-brand-ink mt-4 block">Note (opzionale)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="es. parete in pietra a vista, divano grigio, piante..."
                className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40 focus:border-brand-terracotta"
                data-testid="visualizer-notes"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            {!unlocked ? (
              <VisualizerGate onUnlock={(em) => { setUnlocked(true); setLeadEmail(em); }} />
            ) : (
            <div className="card-tactile p-3 md:p-4">
              {!previewUrl && (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  onClick={() => inputRef.current?.click()}
                  className="aspect-[4/3] rounded-xl border-2 border-dashed border-brand-line bg-brand-bg/60 flex flex-col items-center justify-center cursor-pointer hover:border-brand-terracotta hover:bg-brand-terracotta/5 transition-all p-8"
                  data-testid="visualizer-dropzone"
                >
                  <div className="h-14 w-14 rounded-full bg-brand-terracotta/10 text-brand-terracotta flex items-center justify-center">
                    <Upload className="h-6 w-6" />
                  </div>
                  <div className="heading-display text-xl mt-5">Trascina qui la tua foto</div>
                  <div className="text-sm text-brand-mute mt-1">o clicca per selezionarla (jpg/png, max 10MB)</div>
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPick(e.target.files?.[0])}
                    data-testid="visualizer-file-input"
                  />
                </div>
              )}

              {previewUrl && !resultB64 && !loading && (
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black/5">
                  <img src={previewUrl} alt="anteprima" className="absolute inset-0 w-full h-full object-cover" />
                  <button
                    onClick={reset}
                    className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-full px-3 py-1.5 text-xs font-medium hover:bg-white"
                    data-testid="visualizer-reset"
                  >
                    Cambia foto
                  </button>
                </div>
              )}

              {loading && (
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-brand-ink">
                  {previewUrl && (
                    <img src={previewUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                  )}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center">
                    <Loader2 className="h-10 w-10 animate-spin text-brand-terracotta" />
                    <div className="heading-display text-2xl mt-5 animate-pulse-soft" data-testid="visualizer-stage">
                      {STAGES[stageIdx]}
                    </div>
                    <div className="text-xs uppercase tracking-[0.25em] text-white/60 mt-3">
                      Powered by Gemini · Nano Banana
                    </div>
                  </div>
                </div>
              )}

              {resultB64 && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                      <img src={previewUrl} alt="prima" className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute top-2 left-2 bg-brand-ink/80 text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">Prima</div>
                    </div>
                    <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                      <img
                        src={`data:${resultMime};base64,${resultB64}`}
                        alt="dopo"
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="visualizer-result-image"
                      />
                      <div className="absolute top-2 left-2 bg-brand-terracotta text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">Dopo · AI</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3 mt-4">
                <button onClick={generate} disabled={!file || loading} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="visualizer-generate">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {loading ? "Generazione..." : resultB64 ? "Rigenera" : "Genera ristrutturazione"}
                </button>
                {resultB64 && (
                  <>
                    <button onClick={download} className="btn-secondary" data-testid="visualizer-download">
                      <Download className="h-4 w-4" /> Scarica
                    </button>
                    <button onClick={reset} className="btn-secondary" data-testid="visualizer-restart">
                      <RefreshCw className="h-4 w-4" /> Ricomincia
                    </button>
                  </>
                )}
              </div>

              <p className="text-xs text-brand-mute mt-3">
                * Le immagini generate sono indicative e creative. Il progetto reale verrà definito in sopralluogo.
              </p>
            </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
